import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { Button } from "components/ui/Button";
import { ScheduledSend, cancelScheduledSend, getScheduledSends } from "interfaces/emailing";
import { CategoryBadge, formatDate } from "./parts";

/**
 * Programmed sends still to come (Resend holds them until their time), each cancelable until it
 * leaves. Hidden when there are none.
 */
export default function Scheduled({ refreshKey, onChanged }: { refreshKey: number; onChanged: () => void }) {
    const [rows, setRows] = useState<ScheduledSend[]>([]);
    const [canceling, setCanceling] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getScheduledSends().then(setRows).catch(() => setRows([]));
    }, [refreshKey]);

    if (!rows.length) return null;

    const cancel = async (row: ScheduledSend) => {
        if (!window.confirm(`Annuler l'envoi « ${row.templateName} » à ${row.count} destinataire${row.count > 1 ? "s" : ""} ? C'est définitif.`)) return;
        setCanceling(row.batchId);
        setError(null);
        const res = await cancelScheduledSend(row.batchId);
        setCanceling(null);
        if (res.error) setError(res.error);
        onChanged();
    };

    return (
        <section className="rounded-2xl border border-border bg-surface">
            <h2 className="flex items-center gap-2 border-b border-border px-5 py-3 text-base font-semibold text-ink">
                <CalendarClock className="h-5 w-5 text-ink-muted" />
                Envois programmés
                <span className="text-xs font-normal text-ink-faint">heure de Paris</span>
            </h2>
            <ul className="divide-y divide-border">
                {rows.map((row) => (
                    <li key={row.batchId} className="flex flex-wrap items-center gap-3 px-5 py-3">
                        <span className="w-36 shrink-0 text-sm font-medium text-ink">{formatDate(row.scheduledFor, true)}</span>
                        <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-2 text-sm text-ink">
                                <span className="truncate">{row.templateName}</span>
                                <CategoryBadge category={row.category} />
                            </p>
                            <p className="truncate text-xs text-ink-muted">
                                {row.recipients.join(", ")}
                                {row.count > row.recipients.length && ` et ${row.count - row.recipients.length} autre${row.count - row.recipients.length > 1 ? "s" : ""}`}
                                {row.sentByEmail && ` · par ${row.sentByEmail}`}
                            </p>
                        </div>
                        <Button size="sm" variant="secondary" onClick={() => cancel(row)} disabled={canceling === row.batchId}>
                            {canceling === row.batchId ? "Annulation…" : "Annuler"}
                        </Button>
                    </li>
                ))}
            </ul>
            {error && <p className="px-5 pb-3 text-xs text-danger">{error}</p>}
        </section>
    );
}
